import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  Search, Plus, ExternalLink, MapPin, Euro, Square, Home,
  User, Phone, Mail, MessageSquare, Eye, CheckCircle, XCircle,
  Clock, TrendingUp, AlertTriangle, Filter, RefreshCw, Copy,
  Building, Loader2, ChevronDown, ChevronUp, Star, Sparkles,
  Send, FileText, Calendar, Target, Zap
} from 'lucide-react';

// Status labels and colors
const LEAD_STATUS = {
  new: { label: 'Nuovo', color: 'bg-blue-100 text-blue-700', icon: Sparkles },
  to_review: { label: 'Da Valutare', color: 'bg-yellow-100 text-yellow-700', icon: Eye },
  selected: { label: 'Selezionato', color: 'bg-purple-100 text-purple-700', icon: Star },
  message_generated: { label: 'Messaggio Generato', color: 'bg-indigo-100 text-indigo-700', icon: MessageSquare },
  contacted: { label: 'Contattato', color: 'bg-green-100 text-green-700', icon: Phone },
  interested: { label: 'Interessato', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  not_interested: { label: 'Non Interessato', color: 'bg-red-100 text-red-700', icon: XCircle },
  follow_up: { label: 'Follow-up', color: 'bg-orange-100 text-orange-700', icon: Clock },
  archived: { label: 'Archiviato', color: 'bg-gray-100 text-gray-700', icon: FileText },
};

const SELLER_TYPE = {
  private: { label: 'Privato Verificato', color: 'bg-green-100 text-green-700' },
  agency: { label: 'Agenzia', color: 'bg-red-100 text-red-700' },
  professional: { label: 'Professionista', color: 'bg-orange-100 text-orange-700' },
  unknown: { label: 'Non Verificato', color: 'bg-gray-100 text-gray-700' },
};

// KPI Cards Component
const KPICards = ({ stats, loading }) => {
  const kpis = [
    { 
      label: 'Privati Verificati', 
      value: stats?.total_private_listings || 0, 
      icon: CheckCircle, 
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    { 
      label: 'Nuovi Oggi', 
      value: stats?.new_today || 0, 
      icon: Sparkles, 
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    { 
      label: 'Da Valutare', 
      value: stats?.to_review || 0, 
      icon: Eye, 
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    { 
      label: 'Selezionati', 
      value: stats?.selected || 0, 
      icon: Star, 
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    { 
      label: 'Contattati', 
      value: stats?.contacted || 0, 
      icon: Phone, 
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
      {kpis.map((kpi, idx) => (
        <div key={idx} className={`${kpi.bgColor} rounded-2xl p-4 border border-gray-100`}>
          <div className="flex items-center justify-between mb-2">
            <kpi.icon className={`h-5 w-5 ${kpi.color}`} />
            {loading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          </div>
          <div className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</div>
          <div className="text-xs text-gray-600">{kpi.label}</div>
        </div>
      ))}
    </div>
  );
};

// URL Analysis Form Component
const AnalyzeURLForm = ({ onAnalyze, analyzing }) => {
  const [url, setUrl] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!url.trim()) {
      toast.error('Inserisci un URL valido');
      return;
    }
    onAnalyze(url);
  };

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 mb-6">
      <div className="flex items-center space-x-2 mb-4">
        <Search className="h-5 w-5 text-[#0071e3]" />
        <h2 className="text-lg font-semibold text-gray-900">Analizza Nuovo Annuncio</h2>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <Input
            type="url"
            placeholder="https://www.idealista.it/immobile/... o https://www.immobiliare.it/annunci/..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="w-full"
            data-testid="scout-url-input"
          />
          <p className="text-xs text-gray-500 mt-1">
            Supportati: Idealista.it, Immobiliare.it
          </p>
        </div>
        <Button 
          type="submit" 
          disabled={analyzing || !url.trim()}
          className="bg-[#0071e3] hover:bg-[#0077ED] text-white"
          data-testid="scout-analyze-btn"
        >
          {analyzing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Analisi...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Analizza URL
            </>
          )}
        </Button>
      </form>
    </div>
  );
};

// Analysis Result Dialog
const AnalysisResultDialog = ({ result, open, onClose, onSave, saving }) => {
  if (!result) return null;

  const isPrivate = result.is_private;
  const listingData = result.listing_data || {};

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            {isPrivate ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-red-600" />
            )}
            <span>Risultato Analisi</span>
          </DialogTitle>
          <DialogDescription>
            Verifica dei dati estratti dall'annuncio
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Classification Result */}
          <div className={`p-4 rounded-xl ${isPrivate ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <Badge className={SELLER_TYPE[result.seller_type]?.color || 'bg-gray-100'}>
                  {SELLER_TYPE[result.seller_type]?.label || result.seller_type}
                </Badge>
                <p className="text-sm mt-2">
                  <strong>Metodo:</strong> {result.verification_method}
                </p>
              </div>
              {result.distance_km && (
                <div className="text-right">
                  <div className="text-lg font-bold">{result.distance_km} km</div>
                  <div className="text-xs text-gray-500">dal centro</div>
                </div>
              )}
            </div>
            <p className="text-sm mt-2 text-gray-700">
              <strong>Motivazione:</strong> {result.classification_reason}
            </p>
          </div>

          {/* Property Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-500">Titolo</Label>
              <p className="font-medium text-sm">{listingData.title || 'N/D'}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Prezzo</Label>
              <p className="font-medium text-sm">
                {listingData.price ? `€${listingData.price.toLocaleString('it-IT')}` : 'N/D'}
              </p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Superficie</Label>
              <p className="font-medium text-sm">{listingData.sqm ? `${listingData.sqm} m²` : 'N/D'}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">€/m²</Label>
              <p className="font-medium text-sm">
                {listingData.price_per_sqm ? `€${listingData.price_per_sqm.toLocaleString('it-IT')}` : 'N/D'}
              </p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Locali</Label>
              <p className="font-medium text-sm">{listingData.rooms || 'N/D'}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Indirizzo</Label>
              <p className="font-medium text-sm">{listingData.address || 'N/D'}</p>
            </div>
          </div>

          {/* Features */}
          <div className="flex flex-wrap gap-2">
            {listingData.elevator && <Badge variant="outline">Ascensore</Badge>}
            {listingData.terrace && <Badge variant="outline">Terrazzo</Badge>}
            {listingData.balcony && <Badge variant="outline">Balcone</Badge>}
          </div>

          {/* Images Preview */}
          {listingData.images && listingData.images.length > 0 && (
            <div>
              <Label className="text-xs text-gray-500 mb-2 block">Immagini ({listingData.images.length})</Label>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {listingData.images.slice(0, 5).map((img, idx) => (
                  <img 
                    key={idx} 
                    src={img} 
                    alt={`Foto ${idx + 1}`} 
                    className="h-20 w-20 object-cover rounded-lg flex-shrink-0"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            Chiudi
          </Button>
          {isPrivate && (
            <Button 
              onClick={onSave} 
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="scout-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Salva Lead
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Listing Card Component
const ListingCard = ({ listing, lead, onSelect, onViewDetail }) => {
  const status = LEAD_STATUS[lead?.status] || LEAD_STATUS.new;
  const StatusIcon = status.icon;

  return (
    <div 
      className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => onViewDetail(listing)}
      data-testid={`listing-card-${listing.listing_id}`}
    >
      {/* Image */}
      <div className="relative h-40 bg-gray-100">
        {listing.images && listing.images[0] ? (
          <img 
            src={listing.images[0]} 
            alt={listing.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50" y="50" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="12">No Image</text></svg>';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Building className="h-12 w-12 text-gray-300" />
          </div>
        )}
        
        {/* Status Badge */}
        <div className="absolute top-2 right-2">
          <Badge className={`${status.color} flex items-center space-x-1`}>
            <StatusIcon className="h-3 w-3" />
            <span>{status.label}</span>
          </Badge>
        </div>

        {/* Score Badge */}
        {listing.opportunity_score && (
          <div className="absolute bottom-2 left-2">
            <Badge className={`${
              listing.opportunity_score >= 70 ? 'bg-green-600' : 
              listing.opportunity_score >= 50 ? 'bg-yellow-600' : 'bg-gray-600'
            } text-white`}>
              <Target className="h-3 w-3 mr-1" />
              {listing.opportunity_score}/100
            </Badge>
          </div>
        )}

        {/* Source Badge */}
        <div className="absolute bottom-2 right-2">
          <Badge variant="outline" className="bg-white/90">
            {listing.source === 'idealista' ? 'Idealista' : 'Immobiliare.it'}
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 line-clamp-1 mb-2">
          {listing.title || 'Immobile'}
        </h3>

        <div className="flex items-center text-sm text-gray-500 mb-2">
          <MapPin className="h-3 w-3 mr-1" />
          <span className="line-clamp-1">{listing.address || 'Indirizzo non disponibile'}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-lg font-bold text-[#0071e3]">
            {listing.price ? `€${listing.price.toLocaleString('it-IT')}` : 'Prezzo N/D'}
          </div>
          {listing.sqm && (
            <div className="text-sm text-gray-500">
              {listing.sqm} m²
            </div>
          )}
        </div>

        {listing.price_per_sqm && (
          <div className="text-xs text-gray-400 mt-1">
            €{listing.price_per_sqm.toLocaleString('it-IT')}/m²
          </div>
        )}
      </div>
    </div>
  );
};

// Listing Detail Dialog
const ListingDetailDialog = ({ listing, lead, messages, open, onClose, onGenerateMessage, onUpdateStatus, onMarkContacted, generating }) => {
  const [selectedMessageType, setSelectedMessageType] = useState('professional');
  const [notes, setNotes] = useState(lead?.notes || '');

  if (!listing) return null;

  const status = LEAD_STATUS[lead?.status] || LEAD_STATUS.new;

  const copyMessage = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Messaggio copiato!');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Home className="h-5 w-5 text-[#0071e3]" />
            <span>{listing.title || 'Dettaglio Immobile'}</span>
          </DialogTitle>
          <DialogDescription>
            Gestisci questo lead e genera messaggi personalizzati
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Property Info */}
          <div className="space-y-4">
            {/* Images */}
            {listing.images && listing.images.length > 0 && (
              <div className="relative h-48 bg-gray-100 rounded-xl overflow-hidden">
                <img 
                  src={listing.images[0]} 
                  alt={listing.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-2 right-2">
                  <Badge variant="outline" className="bg-white/90">
                    {listing.images.length} foto
                  </Badge>
                </div>
              </div>
            )}

            {/* Property Details */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 p-3 rounded-xl">
                <div className="flex items-center text-gray-500 text-xs mb-1">
                  <Euro className="h-3 w-3 mr-1" />
                  Prezzo
                </div>
                <div className="font-bold text-lg">
                  {listing.price ? `€${listing.price.toLocaleString('it-IT')}` : 'N/D'}
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-xl">
                <div className="flex items-center text-gray-500 text-xs mb-1">
                  <Square className="h-3 w-3 mr-1" />
                  Superficie
                </div>
                <div className="font-bold text-lg">
                  {listing.sqm ? `${listing.sqm} m²` : 'N/D'}
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-xl">
                <div className="flex items-center text-gray-500 text-xs mb-1">
                  <Home className="h-3 w-3 mr-1" />
                  Locali
                </div>
                <div className="font-bold text-lg">{listing.rooms || 'N/D'}</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-xl">
                <div className="flex items-center text-gray-500 text-xs mb-1">
                  <Target className="h-3 w-3 mr-1" />
                  €/m²
                </div>
                <div className="font-bold text-lg">
                  {listing.price_per_sqm ? `€${listing.price_per_sqm.toLocaleString('it-IT')}` : 'N/D'}
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="bg-blue-50 p-4 rounded-xl">
              <div className="flex items-center text-blue-700 mb-2">
                <MapPin className="h-4 w-4 mr-2" />
                <span className="font-medium">Posizione</span>
              </div>
              <p className="text-sm text-gray-700">{listing.address || 'N/D'}</p>
              {listing.distance_from_center && (
                <p className="text-xs text-gray-500 mt-1">
                  {listing.distance_from_center.toFixed(1)} km dal centro di Roma
                </p>
              )}
            </div>

            {/* Seller Info */}
            <div className="bg-green-50 p-4 rounded-xl">
              <div className="flex items-center text-green-700 mb-2">
                <User className="h-4 w-4 mr-2" />
                <span className="font-medium">Inserzionista</span>
              </div>
              <p className="text-sm text-gray-700">{listing.seller_name || 'Non disponibile'}</p>
              <Badge className={`mt-2 ${SELLER_TYPE[listing.seller_type]?.color}`}>
                {SELLER_TYPE[listing.seller_type]?.label || listing.seller_type}
              </Badge>
              {listing.classification_reason && (
                <p className="text-xs text-gray-500 mt-2">{listing.classification_reason}</p>
              )}
            </div>

            {/* Source Link */}
            <a
              href={listing.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-2 bg-gray-100 hover:bg-gray-200 p-3 rounded-xl transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              <span>Apri su {listing.source === 'idealista' ? 'Idealista' : 'Immobiliare.it'}</span>
            </a>
          </div>

          {/* Right Column - Lead Management */}
          <div className="space-y-4">
            {/* Status */}
            <div className="bg-white border border-gray-200 p-4 rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <Label className="font-medium">Stato Lead</Label>
                <Badge className={status.color}>{status.label}</Badge>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {lead?.status !== 'selected' && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => onUpdateStatus(listing.listing_id, 'selected')}
                    className="text-purple-600 border-purple-200 hover:bg-purple-50"
                  >
                    <Star className="h-3 w-3 mr-1" />
                    Seleziona
                  </Button>
                )}
                {lead?.status !== 'contacted' && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => onMarkContacted(listing.listing_id)}
                    className="text-green-600 border-green-200 hover:bg-green-50"
                  >
                    <Phone className="h-3 w-3 mr-1" />
                    Contattato
                  </Button>
                )}
                {lead?.status !== 'archived' && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => onUpdateStatus(listing.listing_id, 'archived')}
                    className="text-gray-600"
                  >
                    <FileText className="h-3 w-3 mr-1" />
                    Archivia
                  </Button>
                )}
              </div>
            </div>

            {/* AI Message Generation */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-4 rounded-xl border border-indigo-100">
              <div className="flex items-center space-x-2 mb-4">
                <Sparkles className="h-5 w-5 text-indigo-600" />
                <Label className="font-medium text-indigo-900">Genera Messaggio AI</Label>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Select value={selectedMessageType} onValueChange={setSelectedMessageType}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brief">Breve (WhatsApp)</SelectItem>
                    <SelectItem value="professional">Professionale</SelectItem>
                    <SelectItem value="soft">Soft (Consulenziale)</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button
                  onClick={() => onGenerateMessage(listing.listing_id, selectedMessageType)}
                  disabled={generating}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="generate-message-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generazione...
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4 mr-2" />
                      Genera
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Generated Messages */}
            {messages && messages.length > 0 && (
              <div className="space-y-3">
                <Label className="font-medium">Messaggi Generati</Label>
                {messages.map((msg, idx) => (
                  <div key={idx} className="bg-white border border-gray-200 p-4 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline">
                        {msg.message_type === 'brief' ? 'Breve' : 
                         msg.message_type === 'professional' ? 'Professionale' : 'Soft'}
                      </Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyMessage(msg.message)}
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Copia
                      </Button>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {msg.message}
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      {format(new Date(msg.generated_at), "d MMM yyyy 'alle' HH:mm", { locale: it })}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Notes */}
            <div>
              <Label className="font-medium">Note</Label>
              <Textarea
                placeholder="Aggiungi note su questo lead..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-2"
              />
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={() => onUpdateStatus(listing.listing_id, null, notes)}
              >
                Salva Note
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Filters Component
const FiltersBar = ({ filters, setFilters, onApply }) => {
  return (
    <div className="bg-white rounded-2xl p-4 border border-gray-100 mb-6">
      <div className="flex items-center space-x-2 mb-4">
        <Filter className="h-5 w-5 text-gray-500" />
        <span className="font-medium text-gray-700">Filtri</span>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div>
          <Label className="text-xs text-gray-500">Stato</Label>
          <Select value={filters.lead_status || 'all'} onValueChange={(v) => setFilters({...filters, lead_status: v === 'all' ? null : v})}>
            <SelectTrigger>
              <SelectValue placeholder="Tutti" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              {Object.entries(LEAD_STATUS).map(([key, val]) => (
                <SelectItem key={key} value={key}>{val.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs text-gray-500">Portale</Label>
          <Select value={filters.source || 'all'} onValueChange={(v) => setFilters({...filters, source: v === 'all' ? null : v})}>
            <SelectTrigger>
              <SelectValue placeholder="Tutti" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="idealista">Idealista</SelectItem>
              <SelectItem value="immobiliare_it">Immobiliare.it</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs text-gray-500">Prezzo Max</Label>
          <Input
            type="number"
            placeholder="€"
            value={filters.max_price || ''}
            onChange={(e) => setFilters({...filters, max_price: e.target.value ? Number(e.target.value) : null})}
          />
        </div>

        <div>
          <Label className="text-xs text-gray-500">Distanza Max (km)</Label>
          <Input
            type="number"
            placeholder="17"
            value={filters.max_distance || ''}
            onChange={(e) => setFilters({...filters, max_distance: e.target.value ? Number(e.target.value) : null})}
          />
        </div>

        <div>
          <Label className="text-xs text-gray-500">Ordina per</Label>
          <Select value={filters.sort_by || 'newest'} onValueChange={(v) => setFilters({...filters, sort_by: v})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Più recenti</SelectItem>
              <SelectItem value="oldest">Meno recenti</SelectItem>
              <SelectItem value="price_asc">Prezzo crescente</SelectItem>
              <SelectItem value="price_desc">Prezzo decrescente</SelectItem>
              <SelectItem value="score">Punteggio</SelectItem>
              <SelectItem value="distance">Distanza</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-end">
          <Button onClick={onApply} className="w-full bg-[#0071e3] hover:bg-[#0077ED]">
            <Search className="h-4 w-4 mr-2" />
            Applica
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Component
const PrivateListingScout = () => {
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [leads, setLeads] = useState({});
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  const [analysisResult, setAnalysisResult] = useState(null);
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  const [filters, setFilters] = useState({
    source: null,
    max_price: null,
    max_distance: 17,
    lead_status: null,
    sort_by: 'newest',
    page: 1,
    limit: 20
  });
  const [total, setTotal] = useState(0);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get('/admin/scout/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Fetch listings
  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.source) params.append('source', filters.source);
      if (filters.max_price) params.append('max_price', filters.max_price);
      if (filters.max_distance) params.append('max_distance', filters.max_distance);
      if (filters.lead_status) params.append('lead_status', filters.lead_status);
      if (filters.sort_by) params.append('sort_by', filters.sort_by);
      params.append('page', filters.page);
      params.append('limit', filters.limit);

      const response = await axios.get(`/admin/scout/listings?${params.toString()}`);
      setListings(response.data.listings);
      setTotal(response.data.total);
      
      // Build leads map
      const leadsMap = {};
      response.data.listings.forEach(l => {
        if (l.lead) leadsMap[l.listing_id] = l.lead;
      });
      setLeads(leadsMap);
    } catch (error) {
      console.error('Error fetching listings:', error);
      toast.error('Errore nel caricamento degli annunci');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchStats();
    fetchListings();
  }, [fetchStats, fetchListings]);

  // Analyze URL
  const handleAnalyzeURL = async (url) => {
    setAnalyzing(true);
    try {
      const response = await axios.post('/admin/scout/analyze', { url });
      setAnalysisResult(response.data);
      setShowAnalysisDialog(true);
      
      if (!response.data.is_private) {
        toast.warning('Annuncio NON verificato come privato. Scartato per sicurezza.');
      }
    } catch (error) {
      console.error('Error analyzing URL:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'analisi dell\'URL');
    } finally {
      setAnalyzing(false);
    }
  };

  // Save analyzed listing
  const handleSaveListing = async () => {
    if (!analysisResult?.listing_data?.source_url) return;
    
    setSaving(true);
    try {
      await axios.post('/admin/scout/listings', { 
        url: analysisResult.listing_data.source_url 
      });
      toast.success('Lead salvato con successo!');
      setShowAnalysisDialog(false);
      setAnalysisResult(null);
      fetchStats();
      fetchListings();
    } catch (error) {
      console.error('Error saving listing:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // View listing detail
  const handleViewDetail = async (listing) => {
    try {
      const response = await axios.get(`/admin/scout/listings/${listing.listing_id}`);
      setSelectedListing(response.data.listing);
      setSelectedMessages(response.data.messages || []);
      setShowDetailDialog(true);
    } catch (error) {
      console.error('Error fetching listing detail:', error);
      toast.error('Errore nel caricamento del dettaglio');
    }
  };

  // Generate message
  const handleGenerateMessage = async (listingId, messageType) => {
    setGenerating(true);
    try {
      const response = await axios.post(`/admin/scout/leads/${listingId}/generate-message`, {
        message_type: messageType
      });
      setSelectedMessages([...selectedMessages, response.data]);
      toast.success('Messaggio generato!');
    } catch (error) {
      console.error('Error generating message:', error);
      toast.error(error.response?.data?.detail || 'Errore nella generazione del messaggio');
    } finally {
      setGenerating(false);
    }
  };

  // Update lead status
  const handleUpdateStatus = async (listingId, status, notes = null) => {
    try {
      const data = {};
      if (status) data.status = status;
      if (notes !== null) data.notes = notes;
      
      await axios.put(`/admin/scout/leads/${listingId}`, data);
      toast.success(status ? 'Stato aggiornato' : 'Note salvate');
      fetchListings();
      fetchStats();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Errore nell\'aggiornamento');
    }
  };

  // Mark as contacted
  const handleMarkContacted = async (listingId) => {
    try {
      await axios.post(`/admin/scout/leads/${listingId}/contacted`);
      toast.success('Segnato come contattato');
      fetchListings();
      fetchStats();
    } catch (error) {
      console.error('Error marking contacted:', error);
      toast.error('Errore nell\'aggiornamento');
    }
  };

  return (
    <div className="space-y-6" data-testid="private-listing-scout">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Private Listing Scout</h1>
          <p className="text-gray-500 text-sm mt-1">
            Trova e gestisci annunci privati da Idealista e Immobiliare.it
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => { fetchStats(); fetchListings(); }}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
      </div>

      {/* KPI Cards */}
      <KPICards stats={stats} loading={loading} />

      {/* Analyze URL Form */}
      <AnalyzeURLForm onAnalyze={handleAnalyzeURL} analyzing={analyzing} />

      {/* Filters */}
      <FiltersBar 
        filters={filters} 
        setFilters={setFilters} 
        onApply={fetchListings}
      />

      {/* Listings Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#0071e3]" />
        </div>
      ) : listings.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
          <Building className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun annuncio trovato</h3>
          <p className="text-gray-500 mb-4">
            Inizia analizzando un URL da Idealista o Immobiliare.it
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">
              {total} annunci privati verificati
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {listings.map((listing) => (
              <ListingCard
                key={listing.listing_id}
                listing={listing}
                lead={leads[listing.listing_id]}
                onViewDetail={handleViewDetail}
              />
            ))}
          </div>

          {/* Pagination */}
          {total > filters.limit && (
            <div className="flex items-center justify-center space-x-4 mt-6">
              <Button
                variant="outline"
                disabled={filters.page <= 1}
                onClick={() => setFilters({...filters, page: filters.page - 1})}
              >
                Precedente
              </Button>
              <span className="text-sm text-gray-500">
                Pagina {filters.page} di {Math.ceil(total / filters.limit)}
              </span>
              <Button
                variant="outline"
                disabled={filters.page >= Math.ceil(total / filters.limit)}
                onClick={() => setFilters({...filters, page: filters.page + 1})}
              >
                Successiva
              </Button>
            </div>
          )}
        </>
      )}

      {/* Analysis Result Dialog */}
      <AnalysisResultDialog
        result={analysisResult}
        open={showAnalysisDialog}
        onClose={() => setShowAnalysisDialog(false)}
        onSave={handleSaveListing}
        saving={saving}
      />

      {/* Listing Detail Dialog */}
      <ListingDetailDialog
        listing={selectedListing}
        lead={leads[selectedListing?.listing_id]}
        messages={selectedMessages}
        open={showDetailDialog}
        onClose={() => setShowDetailDialog(false)}
        onGenerateMessage={handleGenerateMessage}
        onUpdateStatus={handleUpdateStatus}
        onMarkContacted={handleMarkContacted}
        generating={generating}
      />
    </div>
  );
};

export default PrivateListingScout;
